import { Cookies } from "react-cookie";
import Constants from "./Constants";
import FirstTimeTrigger from "./FirstTimeTrigger";

const DEFAULT_MAX_AGE_DAYS = 7;
// device_id backs multi-session control, so it must outlive a browsing session.
// 400 days is the practical ceiling: Chromium clamps anything longer (RFC 6265bis).
// Safari caps script-written cookies at 7 days regardless — beating that needs a
// server-side Set-Cookie.
const DEVICE_ID_MAX_AGE_DAYS = 400;

const daysFromNow = (days) => new Date(Date.now() + days * 24 * 60 * 60 * 1000);

class CookieManager {
  constructor() {
    this.cookies = new Cookies();
    // In-memory copy of the device id, so getDeviceId stays idempotent for the
    // life of the page even if a cookie read comes back stale. See getDeviceId.
    this._deviceId = null;
  }

  getCookieDomain() {
    if (typeof window === "undefined") return undefined;

    const hostname = window.location.hostname;

    if (hostname === "localhost" || hostname.endsWith(".local")) {
      return hostname;
    }

    const parts = hostname.split(".");
    if (parts.length >= 2) {
      return `.${parts.slice(-2).join(".")}`;
    }

    return hostname;
  }

  set(name, value, options = {}) {
    console.log("[CookieManager] Set cookie:", name, value);

    if (!options.path) {
      options.path = "/";
    }
    if (!options.sameSite) {
      options.sameSite = "None";
    }
    if (!options.secure) {
      options.secure = true;
    }
    if (!options.expires) {
      options.expires = daysFromNow(
        name === Constants.DEVICE_ID ? DEVICE_ID_MAX_AGE_DAYS : DEFAULT_MAX_AGE_DAYS
      );
    }
    if (!options.domain) {
      options.domain = this.getCookieDomain();
    }

    this.cookies.set(name, value, { path: "/", ...options });

    // Keep the cached device id in sync with explicit writes, e.g. Game.js
    // storing the one the landing forwards as ?device_id=.
    if (name === Constants.DEVICE_ID) this._deviceId = value || null;
  }

  get(name) {
    return this.cookies.get(name);
  }

  getAll(options = {}) {
    try {
      return this.cookies.getAll({ doNotParse: false, ...options });
    } catch {
      return {};
    }
  }
  
  remove(name, options = {}) {
    console.log("[CookieManager] Remove cookie:", name);

    if (!options.path) {    
        options.path = "/";
    }
    if (!options.sameSite) {
      options.sameSite = "None";
    }
    if (!options.secure) {
      options.secure = true;
    }
    if (!options.domain) {
      options.domain = this.getCookieDomain();
    }

    this.cookies.remove(name, { path: "/", ...options });

    if (name === Constants.DEVICE_ID) this._deviceId = null;
  }

  exists(name) {
    return !!this.cookies.get(name);
  }

  getAfUserId() {
    const afUserId = this.cookies.get(Constants.APPS_FLYER_ID);
    return afUserId ? afUserId : null;
  }

  // Re-entrant by design: FirstLandingOpen builds the session, which needs the
  // device id, so this calls itself. `_deviceId` is assigned before the trigger
  // so the nested call short-circuits instead of minting a second id and
  // overwriting the cookie (which also duplicated FirstLandingOpen).
  getDeviceId() {
    if (this._deviceId) return this._deviceId;

    const deviceIdCookieName = Constants.DEVICE_ID;
    let deviceId = this.cookies.get(deviceIdCookieName);

    // Re-write an existing id so its expiry slides with use, instead of lapsing a
    // fixed period after it was first created however active the user has been.
    // set() caches it too.
    if (deviceId) {
      this.set(deviceIdCookieName, deviceId);
      return deviceId;
    }

    // Not consent-gated: device_id is app functionality (multi-session control).
    deviceId = this._generateGUID();
    this._deviceId = deviceId;
    console.log("[CookieManager] Generated new device ID:", deviceId);
    this.set(deviceIdCookieName, deviceId);

    if (this.cookies.get(deviceIdCookieName)) {
      console.log("[CookieManager] Cookie saved successfully, triggering FirstLandingOpen");
      FirstTimeTrigger.triggerLogic();
    } else {
      console.warn("[CookieManager] Cookie NOT saved, SKIPPING FirstLandingOpen trigger");
    }

    return deviceId;
  }

  _generateGUID() {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
    }
    return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
          s4() + '-' + s4() + s4() + s4();
  }
}

const cookieManager = new CookieManager();
export default cookieManager;